import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CreateStockItemRequest,
  StockCatalogueItem,
  StockItemDetail,
  StockOwner,
  UpdateStockItemBody,
} from "@shared-core/data/api-types";
import { ApiError } from "@shared-core/data/api/errors";
import { Button } from "../../app/components/primitives/button";
import { Checkbox } from "../../app/components/primitives/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../app/components/primitives/dialog";
import { Label } from "../../app/components/primitives/label";
import {
  CatalogueItemPicker,
  linkedCatalogueValue,
} from "./CatalogueItemPicker";
import { OwnerPicker } from "./OwnerPicker";
import {
  type FieldErrorMap,
  TextField,
  toFieldErrorMap,
} from "./StockFormField";

type FormState = {
  stock_code: string;
  description: string;
  size: string;
  unit_quantity: string;
  pack_quantity: string;
  boxes_per_pallet: string;
  reorder_level: string;
  reorder_quantity: string;
  tracks_batches: boolean;
  /** Chosen when creating; an existing item's owner changes from its page. */
  owner: StockOwner | null;
  catalogue_item: StockCatalogueItem | null;
};

function initialState(item?: StockItemDetail): FormState {
  const text = (value: string | number | null | undefined) =>
    value == null ? "" : String(value);
  return {
    stock_code: text(item?.stock_code),
    description: text(item?.description),
    size: text(item?.size),
    unit_quantity: text(item?.unit_quantity ?? 1),
    pack_quantity: text(item?.pack_quantity ?? 1),
    boxes_per_pallet: text(item?.boxes_per_pallet),
    reorder_level: text(item?.reorder_level),
    reorder_quantity: text(item?.reorder_quantity),
    tracks_batches: item?.tracks_batches ?? false,
    owner: item?.owner ?? null,
    catalogue_item: linkedCatalogueValue(
      item?.repeatable_catalogue_item_id,
      item?.repeatable_catalogue_item,
    ),
  };
}

const optionalText = (value: string) => value.trim() || null;
const optionalInt = (value: string) =>
  value.trim() === "" ? null : Number(value);

/** The request body for the form's current values. Blank optional fields
 * are sent as null; a linked item always tracks batches. */
function toRequest(form: FormState): CreateStockItemRequest {
  return {
    stock_code: form.stock_code,
    description: form.description,
    size: optionalText(form.size),
    unit_quantity: Number(form.unit_quantity || 1),
    pack_quantity: Number(form.pack_quantity || 1),
    boxes_per_pallet: optionalInt(form.boxes_per_pallet),
    reorder_level: optionalInt(form.reorder_level),
    reorder_quantity: optionalInt(form.reorder_quantity),
    tracks_batches: form.tracks_batches || form.catalogue_item !== null,
    owner_customer_code: form.owner?.customer_code ?? "",
    repeatable_catalogue_item_id: form.catalogue_item?.item_id ?? null,
  };
}

/** Only the fields the user changed, so an edit never rewrites what it didn't touch. */
function changedFields(
  form: FormState,
  initial: FormState,
): UpdateStockItemBody {
  const next = toRequest(form);
  const before = toRequest(initial);
  const changes: Record<string, unknown> = {};
  for (const key of Object.keys(next) as (keyof CreateStockItemRequest)[]) {
    // The owner never changes through an edit (it has its own audited action).
    if (key === "owner_customer_code") continue;
    if (next[key] !== before[key]) changes[key] = next[key];
  }
  return changes as UpdateStockItemBody;
}

interface StockItemFormDialogProps {
  /** Present when editing; absent when creating. */
  item?: StockItemDetail;
  onClose: () => void;
  onSaved: (detail: StockItemDetail) => void;
}

export function StockItemFormDialog({
  item,
  onClose,
  onSaved,
}: StockItemFormDialogProps) {
  const dataAdapter = useDataAdapter();
  const [initial] = useState(() => initialState(item));
  const [form, setForm] = useState(initial);
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const isEdit = item != null;
  // Mirrors the backend lock: these two can't change once stock or a
  // position is recorded.
  const flagsLocked = isEdit && item.holdings.length > 0;

  const set = (key: keyof FormState) => (value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));
  const toggle =
    (key: "tracks_batches") => (checked: boolean | "indeterminate") =>
      setForm((prev) => ({ ...prev, [key]: checked === true }));

  async function handleSave() {
    setIsSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      const saved = isEdit
        ? await dataAdapter.updateStockItem(
            item.id,
            changedFields(form, initial),
          )
        : await dataAdapter.createStockItem(toRequest(form));
      onSaved(saved);
    } catch (error) {
      console.error(
        isEdit ? "stock.item.update_failed" : "stock.item.create_failed",
        {
          status: error instanceof ApiError ? error.status : undefined,
        },
      );
      const map = toFieldErrorMap(error);
      setFieldErrors(map);
      setFormError(
        Object.keys(map).length > 0
          ? `Please fix the ${Object.keys(map).length === 1 ? "highlighted field" : "highlighted fields"} and try again.`
          : "Couldn't save this stock item. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Edit ${item.stock_code}` : "New stock item"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "On hand only changes through adjustments and transfers, so it isn't edited here."
              : "The item starts with nothing on hand. Book opening stock in at a warehouse afterwards."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          {formError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300"
            >
              <AlertTriangle className="size-4 shrink-0 mt-0.5" />
              {formError}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="stock-code"
              label="Stock code"
              value={form.stock_code}
              onChange={set("stock_code")}
              error={fieldErrors.stock_code}
              placeholder="e.g. SRA3-170-SILK"
              hint="Letters, numbers and symbols such as - / . # are all fine."
            />
            <TextField
              id="stock-size"
              label="Size (optional)"
              value={form.size}
              onChange={set("size")}
              error={fieldErrors.size}
              placeholder="e.g. SRA3 (320 x 450mm)"
            />
          </div>
          <TextField
            id="stock-description"
            label="Description"
            value={form.description}
            onChange={set("description")}
            error={fieldErrors.description}
          />
          <div className="grid gap-4 sm:grid-cols-3">
            <TextField
              id="stock-unit-quantity"
              label="Units"
              value={form.unit_quantity}
              onChange={set("unit_quantity")}
              error={fieldErrors.unit_quantity}
              inputMode="numeric"
              hint="e.g. 1, 50 or 100"
            />
            <TextField
              id="stock-pack-quantity"
              label="Quantity in pack"
              value={form.pack_quantity}
              onChange={set("pack_quantity")}
              error={fieldErrors.pack_quantity}
              inputMode="numeric"
            />
            <TextField
              id="stock-boxes-per-pallet"
              label="No. boxes per pallet"
              value={form.boxes_per_pallet}
              onChange={set("boxes_per_pallet")}
              error={fieldErrors.boxes_per_pallet}
              inputMode="numeric"
              hint="Optional. A box can hold more than one pack."
            />
          </div>

          <fieldset className="space-y-3 rounded-lg border border-border p-3">
            <legend className="px-1 text-sm font-medium">Batches</legend>
            {
              <FlagField
                id="stock-tracks-batches"
                label="Track batches"
                hint={
                  form.catalogue_item
                    ? "Required while the stock item is linked to a repeatable catalogue item, so each reprint is recorded against the artwork revision the reprint was printed from."
                    : "One batch per receipt, recording the order line, the supplier's purchase order line or the Tharstern job, and the artwork revision the stock was printed from."
                }
                checked={form.tracks_batches || form.catalogue_item !== null}
                onChange={toggle("tracks_batches")}
                disabled={flagsLocked || form.catalogue_item !== null}
                error={fieldErrors.tracks_batches}
              />
            }
            {flagsLocked && (
              <p className="text-xs text-muted-foreground">
                Batches can't change once stock or a bin position is recorded.
              </p>
            )}
          </fieldset>

          {isEdit ? (
            <p className="text-sm text-muted-foreground">
              Owner:{" "}
              {form.owner ? form.owner.name : "a customer no longer active"}.
              Change the owner from the stock item's page, which records who
              changed the owner and why.
            </p>
          ) : (
            <OwnerPicker
              id="stock-owner"
              value={form.owner}
              onChange={(owner) => setForm((prev) => ({ ...prev, owner }))}
              error={fieldErrors.owner_customer_code}
            />
          )}

          <CatalogueItemPicker
            id="stock-catalogue-item"
            value={form.catalogue_item}
            onChange={(catalogueItem) =>
              setForm((prev) => ({ ...prev, catalogue_item: catalogueItem }))
            }
            error={fieldErrors.repeatable_catalogue_item_id}
          />

          {
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                id="stock-reorder-level"
                label="Re-order level (optional)"
                value={form.reorder_level}
                onChange={set("reorder_level")}
                error={fieldErrors.reorder_level}
                inputMode="numeric"
                hint="Flag the item when projected stock falls to this."
              />
              <TextField
                id="stock-reorder-quantity"
                label="Re-order quantity (optional)"
                value={form.reorder_quantity}
                onChange={set("reorder_quantity")}
                error={fieldErrors.reorder_quantity}
                inputMode="numeric"
              />
            </div>
          }
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={isSaving || (!isEdit && form.owner === null)}
          >
            {isSaving
              ? "Saving…"
              : isEdit
                ? "Save changes"
                : "Create stock item"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FlagField({
  id,
  label,
  hint,
  checked,
  onChange,
  disabled = false,
  error,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  onChange: (checked: boolean | "indeterminate") => void;
  disabled?: boolean;
  error?: string;
}) {
  const describedBy = error ? `${id}-error` : `${id}-hint`;
  return (
    <div className="flex items-start gap-2">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className="mt-0.5"
      />
      <div className="space-y-0.5">
        <Label htmlFor={id}>{label}</Label>
        {error ? (
          <p
            id={`${id}-error`}
            className="text-sm text-red-600 dark:text-red-400"
          >
            {error}
          </p>
        ) : (
          <p id={`${id}-hint`} className="text-xs text-muted-foreground">
            {hint}
          </p>
        )}
      </div>
    </div>
  );
}
